import { STORAGE_KEYS } from "@/lib/storage-keys";

type FetchLike = (
	input: RequestInfo | URL,
	init?: RequestInit
) => Promise<Pick<Response, "ok" | "status">>;

interface PrfInvalidateDeps {
	fetch?: FetchLike;
	storage?: Pick<Storage, "getItem" | "removeItem">;
}

const PRF_STORAGE_KEYS = [
	STORAGE_KEYS.PRF_ENCRYPTED_BACKUP,
	STORAGE_KEYS.PRF_CREDENTIAL_ID,
	STORAGE_KEYS.PRF_SALT,
	STORAGE_KEYS.PRF_ENCRYPTED_ACTIVE_ACCOUNT,
] as const;
const BASE64_PADDING_RE = /=+$/u;

function getStorage(
	deps?: PrfInvalidateDeps
): Pick<Storage, "getItem" | "removeItem"> {
	if (deps?.storage) {
		return deps.storage;
	}
	if (typeof localStorage === "undefined") {
		throw new Error("Cannot invalidate PRF state: localStorage is unavailable");
	}
	return localStorage;
}

function getFetch(deps?: PrfInvalidateDeps): FetchLike | null {
	if (deps?.fetch) {
		return deps.fetch;
	}
	if (typeof fetch === "undefined") {
		return null;
	}
	return fetch;
}

function normalizeCredentialId(credentialId: string): string {
	return credentialId
		.trim()
		.replaceAll("+", "-")
		.replaceAll("/", "_")
		.replace(BASE64_PADDING_RE, "");
}

export function isPrfBoundCredential(
	deletedCredentialId: string | null | undefined,
	deps?: PrfInvalidateDeps
): boolean {
	if (!deletedCredentialId) {
		return false;
	}

	const storage = getStorage(deps);
	const boundCredentialId = storage.getItem(STORAGE_KEYS.PRF_CREDENTIAL_ID);
	if (!boundCredentialId) {
		return false;
	}

	return (
		normalizeCredentialId(boundCredentialId) ===
		normalizeCredentialId(deletedCredentialId)
	);
}

export async function invalidateStalePrfState(
	deps?: PrfInvalidateDeps
): Promise<void> {
	const storage = getStorage(deps);
	for (const key of PRF_STORAGE_KEYS) {
		storage.removeItem(key);
	}

	const fetcher = getFetch(deps);
	if (!fetcher) {
		console.warn(
			"PRF credential server cleanup skipped; fetch is unavailable."
		);
		return;
	}

	try {
		const response = await fetcher("/api/webauthn/prf/credential", {
			method: "DELETE",
			credentials: "include",
		});
		if (!(response.ok || response.status === 404)) {
			console.warn(
				"PRF credential server cleanup failed; local biometric unlock state was still removed.",
				{ status: response.status }
			);
		}
	} catch (error) {
		console.warn(
			"PRF credential server cleanup errored; local biometric unlock state was still removed.",
			error
		);
	}
}
